"use client"

import { SVGAttributes, useRef, useState } from "react"
import { Grid, Stack, Typography } from "@mui/material"
import styled from "styled-components"

import { Avatar, isWeb } from "@bullseye/components"
import { colors } from "@bullseye/tailwind-config"

import QuoteIcon from "../../../icons/QuoteIcon"

type UserQuote = {
  quote: string
  name: string
  avatar_url: string
  location: string
}

const CARD_WIDTH = 280

const data: UserQuote[] = [
  {
    avatar_url:
      "https://storage.googleapis.com/hank-app-assets/marketing/david-headshot.jpg",
    location: "Los Angeles, CA",
    name: "David S.",
    quote:
      "I have enjoyed every minute of every Hank event that I have attended, meeting MY PEOPLE, becoming an Ambassador, being a Hank Host. Hank is essential for total well being.",
  },
  {
    avatar_url:
      "https://storage.googleapis.com/hank-app-assets/marketing/steve-headshot.png",
    location: "Boca Raton, FL",
    name: "Steve R.",
    quote:
      "Now that I'm retired, I finally have time to do things and I love it! Hank makes it so easy to find cool events in the community. Every week there's something fun to look forward to!",
  },
  {
    avatar_url:
      "https://storage.googleapis.com/hank-app-assets/marketing/marilyn-headshot.png",
    location: "New York, NY",
    name: "Marilyn D.",
    quote:
      "I went to bed happy, knowing that I have a Hank event the next day. I live alone and it’s important to do things with other people. Thank you.",
  },
  {
    avatar_url:
      "https://storage.googleapis.com/hank-app-assets/marketing/simon-headshot.png",
    location: "Boca Raton, FL",
    name: "Simon B.",
    quote:
      "I was looking for new ways to stay active and meet people. Hank makes it easy to find fun things happening nearby. It’s been a great way to connect with others in the community.",
  },
  {
    avatar_url:
      "https://storage.googleapis.com/hank-app-assets/marketing/andrea-headshot.png",
    location: "New York. NY",
    name: "Andrea D.",
    quote:
      "Such an interesting group. Enjoyed our lunch, too. It was seasonal, fun, and something I needed!",
  },
  {
    avatar_url:
      "https://storage.googleapis.com/hank-app-assets/marketing/linda-headshot.png",
    location: "New York. NY",
    name: "Linda P.",
    quote:
      "I now have more interesting and compatible friends than I did where I moved from.",
  },
  {
    avatar_url:
      "https://storage.googleapis.com/hank-app-assets/marketing/ann-headshot.png",
    location: "New York. NY",
    name: "Ann B.",
    quote: "Hank is easing my retirement transition.",
  },
  {
    avatar_url:
      "https://storage.googleapis.com/hank-app-assets/marketing/norma-headshot.png",
    location: "Brooklyn, NY",
    name: "Norma S.",
    quote:
      "We all clicked, like we knew each other. I felt comfortable. Everyone had stories to talk about.  The host was awesome, I liked her right away.",
  },
  {
    avatar_url:
      "https://storage.googleapis.com/hank-app-assets/marketing/phyllis-headshot.png",
    location: "New York, NY",
    name: "Phyllis M.",
    quote:
      "Hank emboldened me to attend an activity and have conversations with new people!",
  },
  {
    avatar_url:
      "https://storage.googleapis.com/hank-app-assets/marketing/dianne-headshot.png",
    location: "New York, NY",
    name: "Dianne S.",
    quote:
      "I’m getting out more, meeting new people, learning about new places, and doing things in the City.",
  },
  {
    avatar_url:
      "https://storage.googleapis.com/hank-app-assets/marketing/barbara-headshot.png",
    location: "New York, NY",
    name: "Barbara R.",
    quote:
      "I just retired and was concerned about what I would do. Hank has given me places to go to and meet different people.",
  },
]

const Arrow: React.VFC<SVGAttributes<any>> = (props) => (
  <svg
    width="48"
    height="49"
    viewBox="0 0 48 49"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <rect y="0.478516" width="48" height="48" rx="24" fill="white" />
    <path
      d="M19.5 33L28.5 24L19.5 15"
      stroke="#1F1F1F"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const TestimonialCard: React.VFC<UserQuote> = ({
  quote,
  name,
  location,
  avatar_url,
}) => (
  <Stack
    sx={{ backgroundColor: "white", borderRadius: "16px" }}
    p={3}
    pt={5}
    gap={3}
    width={`${CARD_WIDTH}px`}
    minWidth={`${CARD_WIDTH}px`}
  >
    <Grid item>
      <QuoteIcon />
    </Grid>
    <Grid item xs>
      <Typography>{quote}</Typography>
    </Grid>
    <Grid container gap={1.5}>
      <Grid item>
        <Avatar
          className="h-[64px] w-[64px]"
          name={name}
          imageUrl={avatar_url}
        />
      </Grid>
      <Stack gap={0.5}>
        <Typography fontWeight={600}>{name}</Typography>
        <Typography color={colors.gray[600]}>{location}</Typography>
      </Stack>
    </Grid>
  </Stack>
)

const CarouselContainer = styled(Grid)`
  &::-webkit-scrollbar {
    display: none;
  }

  overflow: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;
  width: 100%;
`

const UserTestimonials = () => {
  const isTouchDevice = useRef(
    isWeb() && "ontouchstart" in document.documentElement,
  )
  const carouselRef = useRef<HTMLDivElement>(null)
  const [buttonState, setButtonState] = useState([false, true])

  const clickNext = () => {
    if (!carouselRef.current) {
      return
    }
    const available =
      carouselRef.current.scrollWidth -
      carouselRef.current.offsetWidth -
      carouselRef.current.scrollLeft
    const offset = CARD_WIDTH + 12
    const left =
      carouselRef.current.scrollLeft +
      (available > offset * 1.5 ? offset : available)
    carouselRef.current.scroll({
      left,
      behavior: "smooth",
    })
    setButtonState(() => [true, available > offset])
  }

  const clickPrev = () => {
    if (!carouselRef.current) {
      return
    }
    const offset = CARD_WIDTH + 12
    const scrollLeft = carouselRef.current.scrollLeft
    const left = scrollLeft - (scrollLeft > offset * 1.5 ? offset : scrollLeft)
    carouselRef.current.scroll({
      left,
      behavior: "smooth",
    })
    setButtonState(() => [scrollLeft > offset, true])
  }

  return (
    <Stack
      py={10}
      gap={5}
      sx={{ backgroundColor: colors.brand.indigo[1] }}
      color={"white"}
      alignItems={"center"}
    >
      <Typography
        variant={"h2"}
        maxWidth={"1200px"}
        textAlign={"center"}
        px={{ xs: 2, xl: 0 }}
      >
        <span style={{ color: colors.brand.yellow[1] }}>
          81% of members would recommend Hank
        </span>{" "}
        to a friend. But we’ll let you hear it directly from the{" "}
        <span style={{ textDecoration: "line-through" }}>horse’s</span>{" "}
        community’s mouth:
      </Typography>
      <CarouselContainer item ref={carouselRef}>
        <Grid
          container
          gap={3}
          color={colors.gray[950]}
          flexWrap={"nowrap"}
          px={{ xs: 2, md: 120 / 8 }}
        >
          {data.map((item) => (
            <TestimonialCard key={item.name} {...item} />
          ))}
          {/* empty block in order to have spacing at the end of scroll*/}
          <Grid item minWidth={{ xs: "8px", md: "120px" }}></Grid>
        </Grid>
      </CarouselContainer>
      {!isTouchDevice.current && (
        <Grid container gap={1} justifyContent={"center"}>
          <button
            aria-label="Previous testimonial"
            onClick={buttonState[0] ? clickPrev : undefined}
            disabled={!buttonState[0]}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: buttonState[0] ? "pointer" : "default",
            }}
          >
            <Arrow
              aria-hidden="true"
              style={{
                transform: "scale(-1)",
                opacity: buttonState[0] ? 1 : 0.5,
              }}
            />
          </button>
          <button
            aria-label="Next testimonial"
            onClick={buttonState[1] ? clickNext : undefined}
            disabled={!buttonState[1]}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: buttonState[1] ? "pointer" : "default",
            }}
          >
            <Arrow
              aria-hidden="true"
              style={{
                opacity: buttonState[1] ? 1 : 0.5,
              }}
            />
          </button>
        </Grid>
      )}
    </Stack>
  )
}
export default UserTestimonials
